import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getArInvoicePrint, type ArInvoicePayload } from '../../lib/api';

/**
 * The printable Billing Statement.
 *
 * Renders EXCLUSIVELY from the frozen `payload` a posted billing carries, so a
 * later product rename, price edit or customer correction can never change
 * paper that has already been issued. A draft has no payload and gets a note
 * saying so, rather than a preview: there is no document until it is posted.
 */

function money(v: string | null | undefined): string {
  const n = Number(v ?? 0);
  return (Number.isFinite(n) ? n : 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function longDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' });
}

export function ArInvoicePrintPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ar-invoice-print', id],
    queryFn: () => getArInvoicePrint(id!),
    enabled: Boolean(id),
  });

  const payload = data?.payload as ArInvoicePayload | undefined;

  useEffect(() => {
    // Only ever auto-print issued paper — a draft has no statement at all.
    if (payload) window.print();
  }, [payload]);

  if (isLoading) return <p className="p-8 text-sm text-muted-foreground">Loading…</p>;
  if (isError) return <p className="p-8 text-sm text-destructive">Could not load this billing.</p>;
  if (!payload) {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <h1 className="text-lg font-semibold">Not issued yet</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This billing is still a draft, so there is no statement to print. Post it first — that is
          the moment its figures are frozen.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl bg-white p-8 text-black">
      <header className="flex items-start justify-between border-b pb-4">
        <div>
          <h1 className="text-xl font-bold">{payload.title}</h1>
          <p className="mt-1 font-mono text-sm">{payload.docNo}</p>
        </div>
        <div className="text-right text-sm">
          <div className="font-semibold">{payload.company.name ?? '—'}</div>
          {payload.company.branchName ? <div>{payload.company.branchName}</div> : null}
          {payload.company.address ? <div>{payload.company.address}</div> : null}
          {payload.company.tin ? <div>TIN {payload.company.tin}</div> : null}
          {payload.company.phone ? <div>{payload.company.phone}</div> : null}
        </div>
      </header>

      <section className="grid grid-cols-2 gap-6 py-4 text-sm">
        <div>
          <div className="text-xs uppercase tracking-wide text-neutral-500">Billed to</div>
          <div className="font-semibold">{payload.customer.name ?? '—'}</div>
          {payload.customer.tradeName ? <div>{payload.customer.tradeName}</div> : null}
          {payload.customer.addressLines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
          {payload.customer.tin ? <div>TIN {payload.customer.tin}</div> : null}
          {payload.customer.businessStyle ? (
            <div>Business style: {payload.customer.businessStyle}</div>
          ) : null}
        </div>
        <div className="text-right">
          <div>
            <span className="text-neutral-500">Date: </span>
            {longDate(payload.invoiceDate)}
          </div>
          <div>
            <span className="text-neutral-500">Due: </span>
            {longDate(payload.dueDate)}
          </div>
          {payload.reference ? (
            <div>
              <span className="text-neutral-500">Reference: </span>
              {payload.reference}
            </div>
          ) : null}
        </div>
      </section>

      <table className="w-full text-sm">
        <thead className="border-y text-left text-xs uppercase tracking-wide text-neutral-500">
          <tr>
            <th className="py-2">Description</th>
            <th className="py-2 text-right">Qty</th>
            <th className="py-2 text-right">Unit price</th>
            <th className="py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {payload.lines.map((l, i) => (
            <tr key={i} className="border-b">
              <td className="py-2">
                {l.name}
                {l.sku ? (
                  <span className="ml-2 font-mono text-xs text-neutral-500">{l.sku}</span>
                ) : null}
              </td>
              <td className="py-2 text-right tabular-nums">{l.qty}</td>
              <td className="py-2 text-right tabular-nums">{money(l.unitPrice)}</td>
              <td className="py-2 text-right tabular-nums">{money(l.lineTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <section className="mt-4 flex justify-end">
        <dl className="w-64 space-y-1 text-sm">
          <div className="flex justify-between">
            <dt className="text-neutral-500">Subtotal</dt>
            <dd className="tabular-nums">{money(payload.totals.subtotal)}</dd>
          </div>
          {Number(payload.totals.discount) > 0 ? (
            <div className="flex justify-between">
              <dt className="text-neutral-500">Discount</dt>
              <dd className="tabular-nums">−{money(payload.totals.discount)}</dd>
            </div>
          ) : null}
          {Number(payload.totals.vat) > 0 ? (
            <>
              <div className="flex justify-between">
                <dt className="text-neutral-500">VAT-able sales</dt>
                <dd className="tabular-nums">{money(payload.totals.vatable)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-neutral-500">VAT</dt>
                <dd className="tabular-nums">{money(payload.totals.vat)}</dd>
              </div>
            </>
          ) : null}
          {Number(payload.totals.exempt) > 0 ? (
            <div className="flex justify-between">
              <dt className="text-neutral-500">VAT-exempt</dt>
              <dd className="tabular-nums">{money(payload.totals.exempt)}</dd>
            </div>
          ) : null}
          {Number(payload.totals.zeroRated) > 0 ? (
            <div className="flex justify-between">
              <dt className="text-neutral-500">Zero-rated</dt>
              <dd className="tabular-nums">{money(payload.totals.zeroRated)}</dd>
            </div>
          ) : null}
          <div className="flex justify-between border-t pt-1 text-base font-semibold">
            <dt>Total</dt>
            <dd className="tabular-nums">{money(payload.totals.total)}</dd>
          </div>
        </dl>
      </section>

      {payload.memo ? <p className="mt-6 whitespace-pre-wrap text-sm">{payload.memo}</p> : null}
      <footer className="mt-8 border-t pt-3 text-xs text-neutral-500">{payload.footer}</footer>
    </div>
  );
}
